import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button, Label } from '@monksflow/monks-ui';
import { FileSpreadsheetIcon, UploadCloudIcon, XIcon } from 'lucide-react';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { BUNDLE_EXTENSIONS, isBundle, isPlan, limitFor, PLAN_EXTENSIONS } from '../lib/limits';

const ACCEPTED = [...PLAN_EXTENSIONS, ...BUNDLE_EXTENSIONS].join(',');

/**
 * Mirrors the server's gate. `accept` only filters the file picker, so without this
 * a drop of anything at all is announced to the server as a plan — and because the
 * job record is written before the file is posted, each rejected drop also strands
 * a job.
 */
const buildDroppedPlanSchema = (t: TFunction<'projects'>) =>
  z
    .instanceof(File)
    .refine((file) => isPlan(file) || isBundle(file), {
      message: t('deliveryJobCreate.newJobDialog.dropAnXlsxOrCsvPlan'),
    })
    .refine(
      (file) => {
        const limit = limitFor(file);
        return limit !== undefined && file.size <= limit;
      },
      { message: t('deliveryJobCreate.newJobDialog.thatFileIsOverTheSizeLimit') },
    );

export interface SourceFileFieldProps {
  file: File | null;
  onSelect: (file: File | null) => void;
}

/**
 * One plan or one archive, which is all this dialog took before creatives could travel
 * with it.
 *
 * The off-branch of `delivery_multi_file_job_create`. This file goes whole when the flag
 * graduates, along with the copy keys only it reads: `sourceFile`, `chooseAFile`,
 * `dropAnXlsxCsvOrZipHere`, `dropAnXlsxOrCsvPlan`, `thatFileIsOverTheSizeLimit` and
 * `aFileInTheMonksTemplate`.
 */
export function SourceFileField({ file, onSelect }: SourceFileFieldProps) {
  const { t } = useTranslation('projects');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Both ways in run through the same gate. The picker used to skip it and lean on `accept`,
   * which filters the file list and nothing else, so an oversize sheet chosen through the
   * button reached the server unchecked while the same file dropped was refused.
   */
  const accept = (next: File | undefined) => {
    if (!next) return;

    const checked = buildDroppedPlanSchema(t).safeParse(next);
    if (!checked.success) {
      setError(
        checked.error.issues[0]?.message ??
          t('deliveryJobCreate.newJobDialog.thatFileIsNotAMediaPlan'),
      );
      return;
    }
    setError(null);
    onSelect(checked.data);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="new-job-file">{t('deliveryJobCreate.newJobDialog.sourceFile')}</Label>
      {file ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
          <span className="flex min-w-0 items-center gap-2">
            <FileSpreadsheetIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate text-sm">{file.name}</span>
          </span>
          <Button
            variant="ghost"
            size="mini"
            onClick={() => onSelect(null)}
            data-testid="new-job-clear-file"
          >
            <XIcon className="size-4" />
            <span className="sr-only">{t('deliveryJobCreate.newJobDialog.removeFile')}</span>
          </Button>
        </div>
      ) : (
        <div
          onDragOver={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragging(false);
            accept(event.dataTransfer.files[0]);
          }}
          className={`flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-7 text-center ${
            isDragging ? 'border-primary bg-muted' : 'border-border'
          }`}
        >
          <UploadCloudIcon className="size-6 text-muted-foreground" aria-hidden />
          <span className="text-sm text-muted-foreground">
            {t('deliveryJobCreate.newJobDialog.dropAnXlsxCsvOrZipHere')}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            data-testid="new-job-browse"
          >
            {t('deliveryJobCreate.newJobDialog.chooseAFile')}
          </Button>
          <input
            id="new-job-file"
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              accept(event.target.files?.[0]);
              // Cleared so re-picking the same file after a refusal fires change again.
              event.target.value = '';
            }}
            className="hidden"
            data-testid="new-job-file-input"
          />
        </div>
      )}
      {error && (
        <span role="alert" className="text-xs text-destructive" data-testid="new-job-error">
          {error}
        </span>
      )}
      <span className="text-xs text-muted-foreground">
        {t('deliveryJobCreate.newJobDialog.aFileInTheMonksTemplate')}
      </span>
    </div>
  );
}
